"use client";

import { useEffect, useRef, useState } from "react";
import { emptyResult } from "@/lib/navigation/simulator/matcher";
import type { GraphSummary, LocalizationResult, SimulationDataset, StartArea } from "@/lib/navigation/simulator/types";
import type { VisionEvent } from "@/lib/navigation/vision/types";
import { useSimulatorWorker } from "../Simulator/useSimulatorWorker";

export function useCameraLocalizer(dataset: SimulationDataset | null, area: StartArea | null, range: number, events: VisionEvent[]) {
    const { ready, error: workerError, call } = useSimulatorWorker();
    const generation = useRef(0);
    const [result, setResult] = useState(emptyResult);
    const [history, setHistory] = useState<{ eventId: string; result: LocalizationResult }[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const revision = ++generation.current;
        setResult(emptyResult()); setHistory([]); setError("");
        if (!ready || !dataset) { setBusy(false); return; }
        setBusy(true);
        // Replay is bounded to the session's 128 encounters. This also makes
        // removing a false detection deterministic, including during inference.
        void (async () => {
            try {
                await call<GraphSummary>({ type: "load", dataset, includeSuggestions: false });
                if (generation.current !== revision) return;
                await call({ type: "reset", options: { startArea: area, visibilityRangeMeters: range } });
                const outputs: { eventId: string; result: LocalizationResult }[] = [];
                for (const event of events.filter(event => !event.excluded)) {
                    if (generation.current !== revision) return;
                    outputs.push({ eventId: event.id, result: await call<LocalizationResult>({ type: "observe", observation: event.observation }) });
                }
                if (generation.current === revision) { setHistory(outputs); setResult(outputs.at(-1)?.result ?? emptyResult()); }
            } catch (failure) { if (generation.current === revision) setError(failure instanceof Error ? failure.message : "Помилка локалізації."); }
            finally { if (generation.current === revision) setBusy(false); }
        })();
        return () => { generation.current = revision + 1; };
    }, [ready, dataset, area, range, events, call]);

    return { result, history, busy, error: error || workerError };
}
